import { Box, styled } from '@mui/material';
import MuiBox from '@mui/material/Box';
import { type FC, useState } from 'react';

import { DEBOUNCE_TIME } from '@core/constants/common';
import { useDebounceValue } from '@core/hooks/useDebounceValue';
import {
  DEFAULT_NOTIFICATION_SORT,
  EMPTY_NOTIFICATION_FILTER_VALUES,
  type NotificationFilterValues,
  type NotificationSortOption,
} from '@core/utils/notificationQueryParams';
import type { NotificationScopeObject, NotificationTab } from '@core/utils/notifications';
import { NAVBAR_HEIGHT, NOTIFICATION_PANEL_WIDTH } from '@web/constants/app';
import { useNotificationPanel } from '@web/providers/NotificationPanelProvider/NotificationPanelProvider';

import { NotificationCenterHeader } from './NotificationCenterHeader';
import { NotificationsList } from './NotificationsList';
import { UserReminderDrawer } from './UserReminderDrawer';
import { UserRemindersList } from './UserRemindersList';

const NotificationCenterPanel = styled(MuiBox, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{
  isOpen: boolean;
}>(({ isOpen, theme }) => ({
  position: 'fixed',
  top: NAVBAR_HEIGHT,
  right: 0,
  bottom: 0,
  zIndex: theme.zIndex.appBar - 1,
  width: isOpen ? NOTIFICATION_PANEL_WIDTH : 0,
  overflowX: 'hidden',
  backgroundColor: theme.palette.SlabGray[0],
  borderLeft: isOpen ? `1px solid ${theme.palette.SlabGray.Stroke}` : 'none',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: isOpen
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.down('lg')]: {
    width: isOpen ? '100%' : 0,
  },
}));

export const NotificationCenter: FC = () => {
  const { isOpen, scope, scopeObject } = useNotificationPanel();
  const [tab, setTab] = useState<NotificationTab>('inbox');
  const [search, setSearch] = useState('');
  const appliedSearch = useDebounceValue(search, DEBOUNCE_TIME);
  const [filterValues, setFilterValues] = useState<NotificationFilterValues>(
    EMPTY_NOTIFICATION_FILTER_VALUES,
  );
  const [sortByTab, setSortByTab] = useState(DEFAULT_NOTIFICATION_SORT);
  const sortOption = sortByTab[tab];
  const [reminderID, setReminderID] = useState<string | null>(null);
  const [prefillRecord, setPrefillRecord] = useState<NotificationScopeObject | null>(null);
  const [isReminderDrawerOpen, setIsReminderDrawerOpen] = useState(false);

  const handleEditReminder = (id: string | null): void => {
    setReminderID(id);
    setPrefillRecord(null);
    setIsReminderDrawerOpen(true);
  };

  const handleSortOptionChange = (option: NotificationSortOption): void =>
    setSortByTab((prev) => ({ ...prev, [tab]: option }));

  const handleCreateReminder = (): void => {
    setReminderID(null);
    setPrefillRecord(scope === 'currentObject' ? scopeObject : null);
    setIsReminderDrawerOpen(true);
  };

  return (
    <NotificationCenterPanel isOpen={isOpen}>
      <Box display='flex' flexDirection='column' height='100%' width='100%'>
        <NotificationCenterHeader
          search={search}
          onSearchChange={setSearch}
          tab={tab}
          onTabChange={setTab}
          appliedSearch={appliedSearch}
          filterValues={filterValues}
          onFilterValuesChange={setFilterValues}
          sortOption={sortOption}
          onSortOptionChange={handleSortOptionChange}
          onAddReminder={handleCreateReminder}
        />

        <Box flex={1} minHeight={0} display='flex' flexDirection='column' bgcolor='SlabGray.0'>
          {isOpen &&
            (tab === 'scheduled' ? (
              <UserRemindersList
                search={appliedSearch}
                filterValues={filterValues}
                sortOption={sortOption}
                onEditReminder={handleEditReminder}
              />
            ) : (
              <NotificationsList
                tab={tab}
                search={appliedSearch}
                filterValues={filterValues}
                sortOption={sortOption}
              />
            ))}
        </Box>
      </Box>

      <UserReminderDrawer
        isOpen={isReminderDrawerOpen}
        resourceID={reminderID}
        prefillRecord={prefillRecord}
        onClose={(): void => setIsReminderDrawerOpen(false)}
      />
    </NotificationCenterPanel>
  );
};
